import { NarrativeCommunityHeader } from '../NarrativeCommunityHeader';
import { NarrativeCommunityList } from '../NarrativeCommunityList';
import { useMemo } from 'react';
import { useNarrativeCommunityCompany } from './hooks/useNarrativeCommunityCompany';

import {
  createCompanyCanonicalUrl,
  getNarrativeCommunityHiddenTitle,
  getNarrativeCommunityMeta,
} from './utils';
import { findPrimaryIndustryBySlug } from '../../utils/findPrimaryIndustryBySlug';
import { NarrativeCommunityCompanySearch } from '../NarrativeCommunityCompanySearch';
import { useNarrativeCommunitySort } from './hooks/useNarrativeCommunitySort';
import { NarrativeCommunitySpotlight } from '../NarrativeCommunitySpotlight';
import { NarrativeCommunitySort } from './components/NarrativeCommunitySort';
import { Heading } from '@simplywallst/ui-core/next';

type Props = {
  primaryIndustrySlug: string | undefined | null;
  companyNameSlug: string | undefined | null;
  exchangeTicker: string | undefined | null;
  market: string | undefined | null;
};
export const NarrativeCommunity = ({
  primaryIndustrySlug,
  companyNameSlug,
  exchangeTicker,
  market,
}: Props) => {
  const { primaryIndustryId, primaryIndustryLabel } = useMemo(
    () => findPrimaryIndustryBySlug(primaryIndustrySlug),
    [primaryIndustrySlug]
  );

  const companyCanonicalUrl = createCompanyCanonicalUrl({
    primaryIndustrySlug,
    companyNameSlug,
    exchangeTicker,
    market,
  });

  const { data: companyResponse, isLoading } = useNarrativeCommunityCompany(
    {
      canonicalUrl: companyCanonicalUrl!,
    },
    {
      enabled: !!companyCanonicalUrl,
    }
  );

  const {
    id: companyId,
    name: companyName,
    uniqueSymbol,
    media,
    info,
    narrativeCount,
    primaryNarrativeCount,
    commentCount,
  } = companyResponse?.Company ?? {};

  const { seoTitle, seoDescription, seoImageUrl } = getNarrativeCommunityMeta({
    companyName,
    uniqueSymbol,
    coverImageUrl: media?.find((m) => m.transform === 'main-header')?.url,
    primaryIndustryLabel,
    market,
  });

  const { sort, sortHash, changeSort } = useNarrativeCommunitySort();

  return (
    <div className="grid @container/main">
      <NarrativeCommunityHeader
        primaryIndustryLabel={primaryIndustryLabel}
        companyNameSlug={companyNameSlug}
        market={market}
        primaryIndustrySlug={primaryIndustrySlug}
        exchangeTicker={exchangeTicker}
        companyName={companyName}
        companyId={companyId}
        isLoading={isLoading}
        website={info?.url}
        uniqueSymbol={uniqueSymbol}
        coverImageUrl={seoImageUrl}
        companyCanonicalUrl={companyCanonicalUrl}
        narrativeCount={narrativeCount || 0}
        primaryNarrativeCount={primaryNarrativeCount || 0}
        commentCount={commentCount || 0}
      />
      <div className="px-x2 lg:px-0">
        {!market && <NarrativeCommunitySpotlight />}
        <div className="my-x1 grid grid-cols-[auto_max-content] items-center justify-between gap-x0_5">
          <NarrativeCommunityCompanySearch companyName={companyName} />
          <NarrativeCommunitySort sortHash={sortHash} changeSort={changeSort} />
        </div>
        <Heading as="h2" className="sr-only">
          {getNarrativeCommunityHiddenTitle({
            companyName,
            primaryIndustryLabel,
            market,
          })}
        </Heading>
        <NarrativeCommunityList
          filter={{
            market,
            primaryIndustryId,
            canonicalUrl: companyCanonicalUrl,
          }}
          sort={sort}
          seoTitle={seoTitle}
          seoDescription={seoDescription}
          seoImageUrl={seoImageUrl}
        />
      </div>
    </div>
  );
};
